import type { InvitationEmailOutcome } from "@kwp/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTRPC } from "../../lib/api";
import { usePresence } from "../../lib/use-presence";
import { Button, ErrorText, StatusText } from "../../ui";
import { outcomeMessage, type InviteResult } from "./invite-model";

export type InvitationSender = ReturnType<typeof useInvitationSender>;

/** Confirms presence before an email goes out and refreshes the pending list afterwards. */
export function useInvitationSender() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { ensurePresence } = usePresence();
  const invite = useMutation(trpc.admin.invite.mutationOptions());
  const resend = useMutation(trpc.admin.resendInvitation.mutationOptions());
  /** The invitations list, and any workspace page's members and pending invitations. */
  const refresh = () =>
    Promise.all([queryClient.invalidateQueries({ queryKey: trpc.admin.invitations.queryKey() }), queryClient.invalidateQueries({ queryKey: trpc.workspaces.pathKey() })]);
  /** "cancelled": the passkey prompt was dismissed and nothing was done. */
  const confirm = async () => (await ensurePresence()) === "cancelled";
  return {
    pending: invite.isPending || resend.isPending,
    async invite(input: { email: string; name: string; workspaceId: string; sendFromAccountId: string | null }) {
      if (input.sendFromAccountId !== null && (await confirm())) return "cancelled" as const;
      const { sendFromAccountId, ...rest } = input;
      const out = await invite.mutateAsync({ ...rest, ...(sendFromAccountId === null ? {} : { sendFromAccountId }) });
      await refresh();
      return out;
    },
    async resend(email: string, sendFromAccountId: string): Promise<InvitationEmailOutcome | "cancelled"> {
      if (await confirm()) return "cancelled";
      const out = await resend.mutateAsync({ email, sendFromAccountId });
      await refresh();
      return out;
    },
  };
}

/** The outcome line; a failed email offers "Resend invitation", an approval links to Approvals. */
export function OutcomeLine({
  email,
  outcome,
  invited,
  onResend,
  busy,
}: {
  email: string;
  outcome: InvitationEmailOutcome;
  /** What the invite did; null after a resend. */
  invited: InviteResult | null;
  onResend: (() => void) | null;
  busy: boolean;
}) {
  const { tone, text } = outcomeMessage(email, outcome, invited);
  if (tone === "bad") {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <ErrorText>{text}</ErrorText>
        {onResend === null ? null : (
          <Button onClick={onResend} disabled={busy}>
            Resend invitation
          </Button>
        )}
      </div>
    );
  }
  return (
    <StatusText>
      {text}
      {tone === "approval" ? (
        <>
          {" "}
          <Link to="/approvals" className="text-accent underline-offset-2 hover:underline">
            Review in Approvals
          </Link>
        </>
      ) : null}
    </StatusText>
  );
}
